import { fireAnalytics } from '../firebase.js'
import { getDeviceCategory, getSessionId } from '../analyticsHelpers.ts'

export type AiDocumentAnalysisDomain = 'private_project' | 'materials_quotation' | 'commerce_catalog'

export type AiDocumentAnalysisChannel =
  | 'manual_precarga'
  | 'conversational_chat'
  | 'materials_calculator'

export type AiDocumentAnalysisPhase = 'started' | 'completed' | 'failed'

export type AiDocumentAnalysisFailedOutcome =
  | 'cancelled'
  | 'api_error'
  | 'pipeline_failed'
  | 'timeout'
  | 'unknown'

export type AiDocumentAnalysisContext = {
  domain: AiDocumentAnalysisDomain
  channel: AiDocumentAnalysisChannel
  edifyUserId: string
  draftId?: string
  uploadMode?: string
  precargaPath?: string
  fileCount?: number
  fileName?: string
}

const EVENT_NAME = 'ai_document_analysis'

function baseParams(ctx: AiDocumentAnalysisContext) {
  return {
    edify_user_id: ctx.edifyUserId || undefined,
    session_id: getSessionId(),
    device_category: getDeviceCategory(),
    category: 'ai_document_analysis' as const,
    domain: ctx.domain,
    channel: ctx.channel,
    ...(ctx.draftId ? { draft_id: ctx.draftId } : {}),
    upload_mode: ctx.uploadMode,
    precarga_path: ctx.precargaPath,
    file_count: ctx.fileCount,
    file_name: ctx.fileName
  }
}

function trackAiDocumentAnalysisPhase(
  phase: AiDocumentAnalysisPhase,
  ctx: AiDocumentAnalysisContext,
  extra?: Record<string, unknown>
): void {
  fireAnalytics(EVENT_NAME, {
    ...baseParams(ctx),
    phase,
    action: EVENT_NAME,
    ...extra
  })
}

export function trackAiDocumentAnalysisStarted(ctx: AiDocumentAnalysisContext): void {
  trackAiDocumentAnalysisPhase('started', ctx)
}

export function trackAiDocumentAnalysisCompleted(
  ctx: AiDocumentAnalysisContext,
  extra?: { rubroCount?: number; lineCount?: number; batchPartialIssueCount?: number }
): void {
  trackAiDocumentAnalysisPhase('completed', ctx, {
    rubro_count: extra?.rubroCount,
    line_count: extra?.lineCount,
    batch_partial_issue_count: extra?.batchPartialIssueCount
  })
}

export function trackAiDocumentAnalysisFailed(
  ctx: AiDocumentAnalysisContext,
  args: {
    outcome: AiDocumentAnalysisFailedOutcome
    errorCode?: string
    canContinueWithInsight?: boolean
  }
): void {
  trackAiDocumentAnalysisPhase('failed', ctx, {
    outcome: args.outcome,
    error_code: args.errorCode,
    can_continue_with_insight: args.canContinueWithInsight === true
  })
}

export function resolveAiDocumentAnalysisFailedOutcome(e: unknown): {
  outcome: AiDocumentAnalysisFailedOutcome
  errorCode?: string
  canContinueWithInsight?: boolean
} {
  const named = e as { name?: string; code?: string; documentInsight?: unknown }
  if (named.name === 'AbortError') {
    return { outcome: 'cancelled', errorCode: 'AbortError' }
  }
  const code = typeof named.code === 'string' ? named.code : undefined
  if (code === 'DOCUMENTATION_PIPELINE_FAILED') {
    return {
      outcome: 'pipeline_failed',
      errorCode: code,
      canContinueWithInsight: named.documentInsight != null
    }
  }
  if (code === 'STREAM_TIMEOUT') {
    return { outcome: 'timeout', errorCode: code }
  }
  return { outcome: 'unknown', errorCode: code }
}

export function mapServiceErrorToAiDocumentAnalysisFailed(serviceErr?: {
  code?: string
  documentInsight?: unknown
}): {
  outcome: AiDocumentAnalysisFailedOutcome
  errorCode?: string
  canContinueWithInsight?: boolean
} {
  const code = serviceErr?.code
  if (code === 'DOCUMENTATION_PIPELINE_FAILED') {
    return {
      outcome: 'pipeline_failed',
      errorCode: code,
      canContinueWithInsight: serviceErr?.documentInsight != null
    }
  }
  if (code === 'STREAM_TIMEOUT') {
    return { outcome: 'timeout', errorCode: code }
  }
  if (code === 'DOCUMENTATION_ASYNC_UNAVAILABLE' || code === 'FILE_TOO_LARGE') {
    return { outcome: 'api_error', errorCode: code }
  }
  return { outcome: 'unknown', errorCode: code }
}

export const privateProjectManualPrecargaContext = (
  edifyUserId: string,
  partial: Omit<AiDocumentAnalysisContext, 'domain' | 'channel' | 'edifyUserId'>
): AiDocumentAnalysisContext => ({
  domain: 'private_project',
  channel: 'manual_precarga',
  edifyUserId,
  ...partial
})

export const privateProjectConversationalContext = (
  edifyUserId: string,
  partial: Omit<AiDocumentAnalysisContext, 'domain' | 'channel' | 'edifyUserId'>
): AiDocumentAnalysisContext => ({
  domain: 'private_project',
  channel: 'conversational_chat',
  edifyUserId,
  ...partial
})

export const materialsQuotationCalculatorContext = (
  edifyUserId: string,
  partial: Omit<AiDocumentAnalysisContext, 'domain' | 'channel' | 'edifyUserId'>
): AiDocumentAnalysisContext => ({
  domain: 'materials_quotation',
  channel: 'materials_calculator',
  edifyUserId,
  ...partial
})
